import React, {useEffect, useRef, useState} from "react";
import Header from "../../../components/header/header";
import SettingsSubmenu from "../components/submenu/settings_submenu";
import SettingsSection from "../components/section/section";
import styles from "../styles/settings.module.css";
import axiosInstance from "../../../AxiosInstance";
import Modal from "../../../components/modal/modal";
import {BigTextInput, TextInput} from "../../../components/input/inputs";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faClipboardList, faEdit, faMinusCircle} from "@fortawesome/free-solid-svg-icons";
import {useTranslation} from "react-i18next";

const MODULES = [
    {key: 'companies', labelKey: 'settings.roles.modules.companies', description: 'Company management'},
    {key: 'offices', labelKey: 'settings.roles.modules.offices', description: 'Office and project management'},
    {key: 'devices', labelKey: 'settings.roles.modules.devices', description: 'IoT device management'},
    {key: 'assets', labelKey: 'settings.roles.modules.assets', description: 'Asset and machine management'},
    {key: 'gps', labelKey: 'settings.roles.modules.gps', description: 'GPS tracking and geographic zones'},
    {key: 'operations', labelKey: 'settings.roles.modules.operations', description: 'Automations and workflows'},
    {key: 'dashboards', labelKey: 'settings.roles.modules.dashboards', description: 'Monitoring dashboards'},
    {key: 'historical_data', labelKey: 'settings.roles.modules.historical', description: 'Historical data queries'},
    {key: 'production', labelKey: 'settings.roles.modules.production', description: 'Production metrics and analysis'},
    {key: 'connections', labelKey: 'settings.roles.modules.connections', description: 'MQTT and external API integrations'},
    {key: 'reports', labelKey: 'settings.roles.modules.reports', description: 'Report generation and delivery'},
    {key: 'payments', labelKey: 'settings.roles.modules.payments', description: 'Payment and subscription management'},
    {key: 'users', labelKey: 'settings.roles.modules.users', description: 'User and invitation management'},
    {key: 'roles', labelKey: 'settings.roles.modules.roles', description: 'Role and permission management'},
    {key: 'maintenance', labelKey: 'settings.roles.modules.maintenance', description: 'Maintenance plans, records and spare parts'},
    {key: 'cameras', labelKey: 'settings.roles.modules.cameras', description: 'IP camera management'},
    {key: 'scada', labelKey: 'settings.roles.modules.scada', description: 'SCADA visualization and control'},
];

const ACTIONS = [
    {key: 'can_view', labelKey: 'settings.roles.permissions.view'},
    {key: 'can_create', labelKey: 'settings.roles.permissions.create'},
    {key: 'can_edit', labelKey: 'settings.roles.permissions.edit'},
    {key: 'can_delete', labelKey: 'settings.roles.permissions.delete'},
];

function buildDefaultPermissions(existingPermissions) {
    return MODULES.map(({key}) => {
        const existing = existingPermissions
            ? existingPermissions.find(p => p.module === key)
            : null;
        return {
            module: key,
            can_view: existing ? existing.can_view : false,
            can_create: existing ? existing.can_create : false,
            can_edit: existing ? existing.can_edit : false,
            can_delete: existing ? existing.can_delete : false,
        };
    });
}

function ModulePermission({moduleKey, labelKey, description, permissions, setPermissions}) {
    const {t} = useTranslation();
    const label = t(labelKey);
    const perm = permissions.find(p => p.module === moduleKey) || {
        module: moduleKey, can_view: false, can_create: false, can_edit: false, can_delete: false
    };

    const handleToggle = (actionKey) => {
        setPermissions(prev => prev.map(p =>
            p.module === moduleKey ? {...p, [actionKey]: !p[actionKey]} : p
        ));
    };

    const allChecked = ACTIONS.every(a => perm[a.key]);

    const handleToggleAll = () => {
        const newValue = !allChecked;
        setPermissions(prev => prev.map(p =>
            p.module === moduleKey
                ? {...p, can_view: newValue, can_create: newValue, can_edit: newValue, can_delete: newValue}
                : p
        ));
    };

    return (
        <div style={{border: '1px solid #CBD5E1', borderRadius: '6px', marginBottom: '8px', overflow: 'hidden'}}>
            <div style={{padding: '10px 16px', background: '#F8FAFC', borderBottom: '1px solid #E2E8F0'}}
                 className={'d-flex flex-row justify-content-between align-items-center'}>
                <div className={'d-flex align-items-center'}>
                    <FontAwesomeIcon icon={faClipboardList} color={'#64748B'} size={'lg'} className={'me-3'}/>
                    <div style={{userSelect: 'none'}}>
                        <div style={{color: '#334155', fontWeight: '600', fontSize: '14px'}}>{label}</div>
                        <div style={{color: '#94A3B8', fontSize: '12px'}}>{description}</div>
                    </div>
                </div>
                <div
                    style={{
                        fontSize: '12px',
                        color: allChecked ? '#4F46E5' : '#94A3B8',
                        cursor: 'pointer',
                        userSelect: 'none',
                        fontWeight: '500',
                    }}
                    onClick={handleToggleAll}
                >
                    {allChecked ? t('settings.roles.removeAll') : t('common.all')}
                </div>
            </div>
            <div className={'d-flex flex-row'} style={{padding: '10px 16px', gap: '12px', flexWrap: 'wrap'}}>
                {ACTIONS.map(({key, labelKey: actionLabelKey}) => (
                    <label
                        key={key}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '6px',
                            cursor: 'pointer',
                            userSelect: 'none',
                            fontSize: '13px',
                            color: perm[key] ? '#4F46E5' : '#64748B',
                            fontWeight: perm[key] ? '600' : '400',
                            minWidth: '70px',
                        }}
                    >
                        <input
                            type={'checkbox'}
                            checked={perm[key]}
                            onChange={() => handleToggle(key)}
                            style={{accentColor: '#4F46E5', width: '14px', height: '14px'}}
                        />
                        {t(actionLabelKey)}
                    </label>
                ))}
            </div>
        </div>
    );
}

function PopupSettings({currentRole, setShowPopup, setShowCreateModal}) {
    const {t} = useTranslation();
    const disableRole = () => {
        axiosInstance.put(`role/${currentRole.id}/`, {
            ...currentRole,
            active: !currentRole.active,
        }).then(() => {
            window.location.reload();
        });
    };

    const editRole = () => {
        setShowCreateModal(true);
        setShowPopup(false);
    };

    return (
        <div style={{width: '200px', height: 'auto'}}>
            <div className={styles.popupButton} onClick={() => editRole()}>
                <FontAwesomeIcon icon={faEdit} color={'#64748B'} className={'me-2'}/>
                <div style={{color: '#334155'}}>{t('common.edit')}</div>
            </div>
            {currentRole.name !== 'Super Admin' && (
                <div className={styles.popupButton} onClick={() => disableRole()}>
                    <FontAwesomeIcon icon={faMinusCircle} color={'#64748B'} className={'me-2'}/>
                    <div style={{color: '#334155'}}>{currentRole.active ? t('common.deactivate') : t('common.activate')}</div>
                </div>
            )}
        </div>
    )
}

export default function RoleSettings() {
    const {t} = useTranslation();
    const [showPopup, setShowPopup] = useState(false);
    const [roles, setRoles] = useState([]);
    const [form, setForm] = useState({name: '', description: ''});
    const [permissions, setPermissions] = useState(buildDefaultPermissions(null));
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [mousePosition, setMousePosition] = useState({x: 0, y: 0});
    const popupRef = useRef(null);
    const [currentRole, setCurrentRole] = useState({});

    useEffect(() => {
        const companyId = localStorage.getItem('company_id');
        axiosInstance.get(`role/${companyId ? `?company_id=${companyId}` : ''}`).then((res) => {
            setRoles(res.data.results);
        });
    }, []);

    const openCreateModal = () => {
        setCurrentRole({});
        setForm({name: '', description: ''});
        setPermissions(buildDefaultPermissions(null));
        setShowCreateModal(true);
    };

    const openEditModal = (role) => {
        setCurrentRole(role);
        setForm({name: role.name, description: role.description || '', active: role.active});
        setPermissions(buildDefaultPermissions(role.permissions || []));
        setShowCreateModal(true);
        setShowPopup(false);
    };

    const saveRole = () => {
        const companyId = localStorage.getItem('company_id');
        const payload = {...form, permissions, ...(companyId ? {company_id: companyId} : {})};
        if (Object.keys(currentRole).length > 0) {
            axiosInstance.put(`role/${currentRole.id}/`, payload).then(() => {
                window.location.reload();
            });
        } else {
            axiosInstance.post("role/", payload).then(() => {
                window.location.reload();
            });
        }
    };

    const handleOutsideClick = (event) => {
        if (popupRef.current && !popupRef.current.contains(event.target)) {
            setShowPopup(false);
        }
    };

    useEffect(() => {
        if (showPopup) {
            document.addEventListener('mousedown', handleOutsideClick);
        } else {
            document.removeEventListener('mousedown', handleOutsideClick);
        }
        return () => {
            document.removeEventListener('mousedown', handleOutsideClick);
        };
    }, [showPopup]);

    return (
        <div className={styles.dashboard_container}>
            <Header/>

            {showPopup &&
                <div ref={popupRef} className={styles.popUpModule}
                     style={{top: mousePosition.y + 15, left: mousePosition.x - 180, position: 'absolute', zIndex: '100000'}}>
                    <PopupSettings
                        currentRole={currentRole}
                        setShowCreateModal={() => openEditModal(currentRole)}
                        setShowPopup={setShowPopup}
                    />
                </div>
            }

            <Modal
                show={showCreateModal}
                setter={setShowCreateModal}
                name={Object.keys(currentRole).length > 0 ? t('settings.roles.editRole') : t('settings.roles.createRole')}
                buttonName={t('common.save')}
                onClick={saveRole}
            >
                <TextInput name={'name'} placeholder={t('settings.roles.roleName')} form={form} setForm={setForm}/>
                <BigTextInput name={'description'} placeholder={t('settings.roles.description')} form={form} setForm={setForm}/>

                <div style={{userSelect: 'none'}} className={'mb-3'}>
                    <div className={'mb-3'}>
                        <div style={{color: '#334155', fontWeight: '600'}}>{t('settings.roles.permissionsByModule')}</div>
                        <div style={{color: '#64748B', fontSize: '14px'}}>
                            {t('settings.roles.permissionsDesc')}
                        </div>
                    </div>

                    <div style={{maxHeight: '420px', overflowY: 'auto'}} className={'pe-1'}>
                        {MODULES.map(({key, labelKey, description}) => (
                            <ModulePermission
                                key={key}
                                moduleKey={key}
                                labelKey={labelKey}
                                description={description}
                                permissions={permissions}
                                setPermissions={setPermissions}
                            />
                        ))}
                    </div>
                </div>
            </Modal>

            <div className={styles.dashboardInnerContainer}>
                <SettingsSubmenu active={2}/>

                <SettingsSection title={t('settings.roles.sectionTitle')} description={t('settings.roles.sectionDescription')}>
                    <div side={'left'}>
                        <div className={'d-flex flex-row align-items-center'}>
                            <div className={styles.lightButton} onClick={openCreateModal}>{t('settings.roles.createRole')}</div>
                        </div>
                    </div>
                    <div side={'right'}>
                        <div className={`${styles.squareContainer} p-3`}>
                            {roles.map((role) => (
                                <div key={role.id}
                                     className={`d-flex flex-row align-items-center justify-content-between my-1`}>
                                    <div className={'d-flex align-items-center'}>
                                        <FontAwesomeIcon icon={faClipboardList} color={`#64748B`} size={'xl'}
                                                         className={'me-3'}/>
                                        <div style={{userSelect: "none"}}>
                                            <div style={{color: '#334155', fontWeight: '600'}}>{role.name}</div>
                                            <div style={{color: '#64748B', fontSize: '14px'}}>
                                                {!role.active && <span style={{color: '#EF4444', marginRight: '6px'}}>{t('common.inactive')}</span>}
                                                {role.count > 0 && t('settings.roles.userCount', {count: role.count})}
                                            </div>
                                        </div>
                                    </div>
                                    <div className={`d-flex flex-column ps-3 ${styles.editButton}`}
                                         onClick={(event) => {
                                             setShowPopup(true);
                                             setMousePosition({x: event.clientX, y: event.clientY});
                                             setCurrentRole(role);
                                         }}/>
                                </div>
                            ))}
                        </div>
                    </div>
                </SettingsSection>
            </div>
        </div>
    );
}
